import { useEffect, useRef, useState } from "react";
import { logger } from "@artis/logger";

import type {
  MarketUserPermissionsSubSubscription,
  PermissionFolderDetailsQueryQuery,
} from "../../__generated__/gql/graphql";
import { usePermissionFolderDetails, usePermissionRowsSub } from "../../data/user";
import { useUserId } from "../../context/auth/hooks";
import type { FolderAccessBaseline, ProductChangeAlert } from "../calcs/lib/features/product-alerts";
import { createPermissionTableSnapshotStore } from "../calcs/lib/features/product-alerts/snapshot-store";
import { useNotificationCentreEnabled } from "./feature-flag";
import {
  detectPermissionTableChanges,
  fingerprintFolderAccessSnapshot,
  packagesNeedingDetails,
  permissionTableSnapshot,
  type PermissionTableRow,
} from "./permissionTableAlerts";

type FolderDetails = PermissionFolderDetailsQueryQuery["permission"];

const mapLocalRows = (
  rows: MarketUserPermissionsSubSubscription["permission"],
  details: FolderDetails = [],
): PermissionTableRow[] => {
  const folders = new Map(details.map((detail) => [detail.package, detail.packageByPackage]));
  return rows.map((row) => {
    const folder = folders.get(row.package);
    return {
      packageId: String(row.package),
      folderName: folder?.name ?? null,
      packageType: folder?.package_type ?? null,
      permission: row.permission,
      curves: folder?.products.map((product) => ({ id: product.id, name: product.name })) ?? [],
      curveCount: folder?.products_aggregate.aggregate?.count ?? folder?.products.length ?? 0,
    };
  });
};

export const usePermissionTableAlerts = (onAlerts: (alerts: ProductChangeAlert[]) => void) => {
  const enabled = useNotificationCentreEnabled();
  const userId = useUserId();
  const active = enabled && Boolean(userId);
  const local = usePermissionRowsSub(userId, active);
  const fetchDetails = usePermissionFolderDetails();
  const lastKnown = useRef<FolderAccessBaseline>(new Map());
  const established = useRef(false);
  const lastFingerprint = useRef<string | null>(null);
  const store = useRef(createPermissionTableSnapshotStore());
  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    if (!active || !userId) {
      setIsRestored(false);
      return;
    }
    let cancelled = false;
    lastKnown.current = new Map();
    lastFingerprint.current = null;
    established.current = false;
    setIsRestored(false);
    void store.current.load(userId).then((baseline) => {
      if (cancelled) return;
      if (baseline) {
        lastKnown.current = new Map(baseline);
        established.current = true;
      }
      setIsRestored(true);
    });
    return () => {
      cancelled = true;
    };
  }, [active, userId]);

  useEffect(() => {
    if (!active || !userId || !isRestored) return;

    const localReady = local.data != null || local.error != null;
    if (!localReady) return;

    const localRows = local.data?.permission;
    if (!localRows) return;

    const bare = permissionTableSnapshot(mapLocalRows(localRows));
    const fingerprint = fingerprintFolderAccessSnapshot(bare);
    if (fingerprint === lastFingerprint.current) return;

    const apply = (next: typeof bare) => {
      lastFingerprint.current = fingerprint;
      const alerts = detectPermissionTableChanges(lastKnown.current, next, established.current);
      if (!established.current) {
        logger.info("Permission baseline established, changes are reported from here", {
          tags: ["webapp"],
          folders: next.size,
        });
      }
      established.current = true;
      void store.current.save(userId, lastKnown.current);
      if (alerts.length > 0) onAlerts(alerts);
    };

    const packages = packagesNeedingDetails(lastKnown.current, bare, established.current).map(Number);
    if (packages.length === 0) {
      apply(bare);
      return;
    }

    let cancelled = false;
    fetchDetails(userId, packages)
      .then((details) => permissionTableSnapshot(mapLocalRows(localRows, details)))
      .catch((error) => {
        logger.error("Permission folder details fetch failed, alerting without them", error as Error, {
          tags: ["webapp"],
        });
        return bare;
      })
      .then((next) => {
        if (!cancelled) apply(next);
      });
    return () => {
      cancelled = true;
    };
  }, [active, userId, isRestored, local.data, local.error, onAlerts, fetchDetails]);
};
